import {
  Box,
  Typography,
  MenuItem,
  Select,
  Divider,
  IconButton,
  Popper,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useEffect, useMemo, useRef, useState } from "react";
import CommonBarChart from "../Common/CommonBarChart";
import "../../styles/analytics.css";
import { ActionItemGroupByKey } from "../../utils/utils";
import { ChartDataItem, Options, SelectedChartBar } from "../../utils/types";

interface GroupByOption {
  label: string;
  value: ActionItemGroupByKey;
}
interface AnalyticsBarCardProps {
  title: string;
  totalCount: number;
  groupBy: ActionItemGroupByKey;
  groupByOptions: readonly GroupByOption[];
  onGroupByChange: (val: ActionItemGroupByKey) => void;
  chartData: ChartDataItem[];
  loading: boolean;
  onBarClick?: (item: { key?: string; label: string; value: number }) => void;
  selectedChartBar?: SelectedChartBar | null;
  isSeventhGearUser: any;
  showColorBox?: boolean;
  selectedColor?: string;
  selectedKey?: string;
  labelSuffix?: string;
  enableOthersDots?: boolean;
  hoverText?: string;
  barChartHeight?: number;
  showPagination?: boolean;
  offset?: number;
  limit?: number;
  totalRecords?: number;
  onOffsetChange?: (offset: number) => void;
  disablePaginationControls?: boolean;
  accountFilterOptions?: Options[];
  selectedAccountValue?: string;
  onAccountFilterChange?: (value?: string) => void;
}

const AnalyticsBarCard = ({
  title,
  totalCount,
  groupBy,
  groupByOptions,
  onGroupByChange,
  chartData,
  loading,
  onBarClick,
  selectedChartBar,
  showColorBox = false,
  selectedColor,
  selectedKey,
  labelSuffix = "Tasks",
  enableOthersDots = false,
  hoverText,
  barChartHeight,
  showPagination = false,
  offset = 1,
  limit = 10,
  totalRecords = 0,
  onOffsetChange,
  disablePaginationControls = false,
  accountFilterOptions = [],
  selectedAccountValue,
  onAccountFilterChange,
}: AnalyticsBarCardProps) => {
  // Determine if we should show the color box and what text color to use
  const isTotal = selectedKey === "total";
  const isNotRated = selectedKey === "not_rated";
  const shouldShowBox = showColorBox && selectedColor && !isTotal;
  const textColor = (isTotal || isNotRated) ? "#000000" : (showColorBox ? selectedColor : "#6E56CF");
  const safeOffset = Math.max(1, offset);
  const safeLimit = Math.max(1, limit);
  const showingFrom = totalRecords > 0 ? (safeOffset - 1) * safeLimit + 1 : 0;
  const showingTo = Math.min(safeOffset * safeLimit, totalRecords);
  const canGoPrevious = showPagination && !disablePaginationControls && safeOffset > 1;
  const canGoNext = showPagination && !disablePaginationControls && showingTo < totalRecords;
  const [dotsAnchorEl, setDotsAnchorEl] = useState<HTMLElement | null>(null);
  const [isDotsDropdownOpen, setIsDotsDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const popperRef = useRef<HTMLDivElement>(null);

  const filteredAccountOptions = useMemo(() => {
    if (!searchText.trim()) return accountFilterOptions;
    return accountFilterOptions.filter((option) =>
      option.label.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [accountFilterOptions, searchText]);

  const shouldHideUntilSearch = accountFilterOptions.length > 5 && searchText.trim() === "";

  useEffect(() => {
    if (!isDotsDropdownOpen) {
      setSearchText("");
    }
  }, [isDotsDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const isInsideAnchor = dotsAnchorEl?.contains(targetNode);
      const isInsideDropdown = popperRef.current?.contains(targetNode);

      if (!isInsideAnchor && !isInsideDropdown) {
        setIsDotsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dotsAnchorEl]);

  return (
    <Box className="analytics-bar-card" sx={{ opacity: loading ? 0.5 : 1 }}>
      {/* Header */}
      <Box>
        <Box display="flex" alignItems="center" paddingLeft="15px" gap="14px">
          <Typography className="analytics-status-text">
            Breakdown of :{" "}
            <span style={{ color: "#6E56CF", display: "inline-flex", alignItems: "center", gap: "6px" }}>
              {totalCount}{" "}
              {shouldShowBox && (
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: "14px",
                    height: "14px",
                    borderRadius: "3px",
                    backgroundColor: selectedColor,
                    verticalAlign: "middle",
                  }}
                />
              )}
              <span style={{ color: textColor }}>
                {title}
              </span>{" "}
              <span
              style={{
                color: labelSuffix === "Interactions" ? "#000000" : textColor,
              }}
            >
              {labelSuffix}
            </span>
            </span>{" "}
            by
          </Typography>
          <Select
            size="small"
            value={groupBy}
            onChange={(e) =>
              onGroupByChange(e.target.value as ActionItemGroupByKey)
            }
            MenuProps={{
              disableScrollLock: true,
            }}
          >
            {groupByOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Divider className="graph-divider" />
      </Box>
      <Box>
      {dotsAnchorEl && (
        <Popper
          open={isDotsDropdownOpen}
          anchorEl={dotsAnchorEl}
          placement="top-end"
          disablePortal={false}
          sx={{ zIndex: 999 }}
          modifiers={[
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
                padding: 8,
              },
            },
            {
              name: "flip",
              enabled: false,
            },
            {
              name: "offset",
              options: {
                offset: [-180, 100],
              },
            },
          ]}
        >
          <div ref={popperRef}>
            <div className="fp-dropdown fp-account-search-dropdown">
              <div className="fp-search-wrapper">
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="fp-search-input"
                  autoFocus
                />
              </div>
              {shouldHideUntilSearch ? (
                <div className="fp-no-results">Type to search accounts</div>
              ) : filteredAccountOptions.length > 0 ? (
                filteredAccountOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`fp-option ${
                      selectedAccountValue === option.value ? "active" : ""
                    }`}
                    onClick={() => {
                      onAccountFilterChange?.(option.value);
                      setIsDotsDropdownOpen(false);
                    }}
                  >
                    <div className="fp-option-content">
                      <h1 className="fp-dropdown-text">{option.label}</h1>
                    </div>
                    <span
                      className={`fp-option-circle ${
                        selectedAccountValue === option.value ? "filled" : "empty"
                      }`}
                    ></span>
                  </div>
                ))
              ) : (
                <div className="fp-no-results">No accounts found</div>
              )}
            </div>
          </div>
        </Popper>
      )}
      {showPagination && (
            <Box className="analytics-bar-card__pagination">
             <Typography className="analytics-bar-card__pagination-text">
                {`Showing ${showingFrom} - ${showingTo} Of ${totalRecords}`}
              </Typography>
              <IconButton
                size="small"
                disabled={!canGoPrevious}
                onClick={() =>
                  onOffsetChange?.(Math.max(1, safeOffset - 1))
                }
               className="analytics-bar-card__pagination-button"
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                size="small"
                disabled={!canGoNext}
                onClick={() => onOffsetChange?.(safeOffset + 1)}
              className="analytics-bar-card__pagination-button"
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}
        <CommonBarChart
          data={chartData}
          hoverColor="#5731C3"
          fadedColor="#CEC1F2"
          loading={loading}
          onBarClick={onBarClick}
          height={barChartHeight}
          selectedBarKey={selectedChartBar?.key}
          enableOthersDots={enableOthersDots}
          groupBy={groupBy}  
          hoverText={hoverText}
          dotsHighlighted={
            isDotsDropdownOpen ||
            (!!selectedChartBar && selectedChartBar.groupBy === ActionItemGroupByKey.CLIENT)
          }
          onOthersDotsClick={(anchorEl) => {
            setDotsAnchorEl(anchorEl);
            setIsDotsDropdownOpen(true);
          }}
        />
      </Box>
    </Box>
  );
};

export default AnalyticsBarCard;
